import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';

const MovieEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [genres, setGenres] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [thumbnailPreview, setThumbnailPreview] = useState('');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        director: '',
        thumbnailUrl: '',
        backgroundUrl: '',
        videoUrl: '',
        releaseDate: '',
        duration: 0,
        genreIds: []
    });

    // Fetch genres
    useEffect(() => {
        fetch(`${baseUrl}/genres`)
            .then(res => res.json())
            .then(data => setGenres(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching genres:', err));
    }, []);

    // Fetch movie info
    useEffect(() => {
        const fetchMovie = async () => {
            try {
                const res = await fetch(`${baseUrl}/movies/${id}`);
                const data = await res.json();

                // Ánh xạ tên thể loại sang ID
                const matchedGenreIds = genres
                    .filter((g) => data.genres.includes(g.name))
                    .map((g) => g.id);

                setFormData({
                    title: data.title || '',
                    description: data.description || '',
                    director: data.director || '',
                    thumbnailUrl: data.thumbnailUrl || '',
                    backgroundUrl: data.backgroundUrl || '',
                    videoUrl: data.videoUrl || '',
                    releaseDate: data.releaseDate || '',
                    duration: data.duration || 0,
                    genreIds: matchedGenreIds
                });

                // Thumbnail preview
                if (data.thumbnailUrl) {
                    setThumbnailPreview(data.thumbnailUrl);
                }
            } catch (err) {
                console.error('Lỗi lấy dữ liệu phim:', err);
            }
        };

        if (genres.length > 0) {
            fetchMovie();
        }
    }, [id, genres]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleGenreChange = (genreId) => {
        setFormData(prev => {
            const newGenreIds = prev.genreIds.includes(genreId)
                ? prev.genreIds.filter(id => id !== genreId)
                : [...prev.genreIds, genreId];
            return { ...prev, genreIds: newGenreIds };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem("jwtToken");

            const res = await fetch(`${baseUrl}/movies/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Cập nhật thất bại');
            }

            Swal.fire('Thành công!', 'Phim đã được cập nhật.', 'success')
                .then(() => navigate('/admin'));

        } catch (err) {
            console.error('Update error:', err);
            Swal.fire('Lỗi', err.message || 'Có lỗi xảy ra khi cập nhật phim', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-dashboard">
            <div className="back-button-container">
                <Link to="/admin" className="back-button">
                    &larr; Quay về trang quản trị
                </Link>
            </div>

            <div className="movie-add-container">
                <h1>Cập nhật phim</h1>

                <form onSubmit={handleSubmit} className="movie-form">
                    <div className="form-group">
                        <label>Tiêu đề phim *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Mô tả</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Đạo diễn</label>
                        <input
                            type="text"
                            name="director"
                            value={formData.director}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Ảnh đại diện</label>
                        {thumbnailPreview && (
                            <div className="thumbnail-preview">
                                <img src={thumbnailPreview} alt="thumbnail" />
                                <p>{formData.thumbnailUrl}</p>
                            </div>
                        )}
                        <input
                            type="text"
                            name="thumbnailUrl"
                            value={formData.thumbnailUrl}
                            onChange={handleChange}
                            placeholder="Tên file ảnh"
                        />
                    </div>

                    <div className="form-group">
                        <label>URL background *</label>
                        <input
                            type="url"
                            name="backgroundUrl"
                            value={formData.backgroundUrl}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>URL video *</label>
                        <input
                            type="url"
                            name="videoUrl"
                            value={formData.videoUrl}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Ngày phát hành</label>
                        <input
                            type="date"
                            name="releaseDate"
                            value={formData.releaseDate}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Thời lượng (phút) *</label>
                        <input
                            type="number"
                            name="duration"
                            value={formData.duration}
                            onChange={handleChange}
                            min="1"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Thể loại</label>
                        <div className="genre-checkboxes">
                            {genres.map(genre => (
                                <div key={genre.id} className="genre-checkbox">
                                    <input
                                        type="checkbox"
                                        id={`genre-${genre.id}`}
                                        checked={formData.genreIds.includes(genre.id)}
                                        onChange={() => handleGenreChange(genre.id)}
                                    />
                                    <label htmlFor={`genre-${genre.id}`}>{genre.name}</label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={loading}
                        >
                            {loading ? 'Đang cập nhật...' : 'Cập nhật phim'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MovieEdit;
